import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

/**
 * Date range picker with preset periods and custom date selection
 */
const DateRangePicker = ({ startDate, endDate, onChange }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(startDate);
  const [customEnd, setCustomEnd] = useState(endDate);

  const presets = [
    { label: 'Last 30 Days', days: 30 },
    { label: 'Last 90 Days', days: 90 },
    { label: 'Last 6 Months', days: 180 },
    { label: 'Last 12 Months', days: 365 },
    { label: 'Year to Date', ytd: true }
  ];

  const handlePresetClick = (preset) => {
    const end = new Date();
    let start;

    if (preset.ytd) {
      start = new Date(end.getFullYear(), 0, 1);
    } else {
      start = new Date(end.getTime() - preset.days * 24 * 60 * 60 * 1000);
    }

    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];

    setCustomStart(startStr);
    setCustomEnd(endStr);
    setShowCustom(false);
    onChange(startStr, endStr);
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      onChange(customStart, customEnd);
      setShowCustom(false);
    }
  };

  const formatDateRange = () => {
    if (!startDate || !endDate) return 'Select date range';
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowCustom(!showCustom)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
      >
        <Calendar size={18} className="text-slate-500" />
        <span className="text-sm font-medium text-slate-700">{formatDateRange()}</span>
        <ChevronDown size={16} className="text-slate-400" />
      </button>

      {showCustom && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 p-4 z-50">
          <div className="mb-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Quick Select</p>
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handlePresetClick(preset)}
                  className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-blue-100 hover:text-blue-700 transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Custom Range</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <button
              onClick={handleCustomApply}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Apply Range
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
